import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ShipmentStatusBadge from './ShipmentStatusBadge';
import ShipmentCardMenu from './ShipmentCardMenu';
import ShipmentLabelList from './ShipmentLabelList';
import { useShipmentDateFormat } from '../../hooks/useShipmentDateFormat';
import type { ShipmentListItemView } from '../../api/generated/models/ShipmentListItemView';
import type { ShipmentStatusRequest } from '../../api/generated/models/ShipmentStatusRequest';

interface ShipmentCardProps {
  shipment: ShipmentListItemView;
  canWrite: boolean;
  draggable?: boolean;
  isDragging?: boolean;
  onDragStart?: (id: string) => void;
  onDragEnd?: () => void;
  onChangeStatus: (id: string, status: ShipmentStatusRequest.executionStatus) => void;
  onArchive: (id: string) => void;
}

const ShipmentCard: React.FC<ShipmentCardProps> = ({
  shipment,
  canWrite,
  draggable = false,
  isDragging = false,
  onDragStart,
  onDragEnd,
  onChangeStatus,
  onArchive,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatDate = useShipmentDateFormat();
  const didDragRef = useRef(false);

  const openDetail = () => {
    if (didDragRef.current) return;
    navigate(`/features/shipment-tracker/shipments/${shipment.id}`);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    didDragRef.current = true;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', shipment.id);
    onDragStart?.(shipment.id);
  };

  const handleDragEnd = () => {
    window.setTimeout(() => {
      didDragRef.current = false;
    }, 0);
    onDragEnd?.();
  };

  const isDelivered = shipment.executionStatus === 'delivered';
  const dateLine = isDelivered
    ? t('shipment_card_arrived', {
        date: formatDate(shipment.deliveredAt ?? shipment.eta) || '—',
      })
    : t('shipment_card_eta', {
        date: formatDate(shipment.eta) || '—',
      });

  const titleText = [shipment.reference, shipment.name].filter(Boolean).join(' ');

  return (
    <div
      className={`shipment-card shipment-card--clickable${isDragging ? ' shipment-card--dragging' : ''}${draggable ? ' shipment-card--draggable' : ''}`}
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onDragEnd={draggable ? handleDragEnd : undefined}
      onClick={openDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDetail();
        }
      }}
      role="link"
      tabIndex={0}
    >
      <div className="shipment-card__header">
        <span className="shipment-card__name">{titleText}</span>
        {canWrite && (
          <ShipmentCardMenu
            currentStatus={shipment.executionStatus}
            onChangeStatus={(status) => onChangeStatus(shipment.id, status)}
            onArchive={() => onArchive(shipment.id)}
          />
        )}
      </div>
      <div className="shipment-card__eta">{dateLine}</div>
      <div className="shipment-card__badges">
        <ShipmentStatusBadge status={shipment.executionStatus ?? null} />
        <ShipmentLabelList labels={shipment.labels ?? []} max={2} />
      </div>
    </div>
  );
};

export default ShipmentCard;
